import React, { useEffect } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, useLocation } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";
import SubscriptionGuard from "./components/SubscriptionGuard";
import { trackPageView } from "./utils/analyticsService";
import { MerchProvider } from "./contexts/MerchContext";

import UserRegistration from "./pages/user-registration";
import UserLogin from "./pages/user-login";
import AuthCallback from "./pages/auth-callback";
import AccountSetup from "./pages/account-setup";
import GameLibraryDashboard from "./pages/game-library-dashboard";
import GameDetailLaunch from "./pages/game-detail-launch";
import SubscriptionManagement from "./pages/subscription-management";
import AdminDashboard from "./pages/admin-dashboard";
import AdminUserManagement from "./pages/admin-user-management";
import AdminModerationDashboard from "./pages/admin-moderation-dashboard";
import AdminStudioManagement from "./pages/admin-studio-management";
import AdminStudioDetails from "./pages/admin-studio-details";
import AdminGameReviews from "./pages/admin-game-reviews";
import AdminRevenueAnalytics from "./pages/admin-revenue-analytics";
import AdminContactManagement from "./pages/admin-contact-management";
import AdminEmailTemplates from "./pages/admin-email-templates";
import AdminSiteMessages from "./pages/admin-site-messages";
import UserProfileSettings from "./pages/user-profile-settings";
import GameContentManagement from "./pages/game-content-management";
import StudioRevenueAnalytics from "./pages/studio-revenue-analytics";
import MyLibrary from "./pages/library";
import SearchResults from "./pages/search-results";
import NotificationsPage from "./pages/notifications";
import AdminGuard from "./components/AdminGuard";
import NotFound from "./pages/NotFound";
import PrivacyPolicy from "./pages/privacy-policy";
import TermsOfUse from "./pages/terms-of-use";
import CommunityGuidelines from "./pages/community-guidelines";
import ContactPage from "./pages/contact";
import DevelopersPage from "./pages/developers";
import AnalyticsShowcase from "./pages/developers/AnalyticsShowcase";
import PlatformComparison from "./pages/developers/PlatformComparison";
import MonetizationShowcase from "./pages/developers/MonetizationShowcase";
import IAPIntegrationGuide from "./pages/developers/IAPIntegrationGuide";
import PayoutProcessGuide from "./pages/developers/PayoutProcessGuide";
import StudioDashboard from "./pages/studio-dashboard";
import StudioGames from "./pages/studio-games";
import StudioGameUpload from "./pages/studio-game-upload";
import StudioAnalytics from "./pages/studio-analytics";
import StudioIntelligenceHub from "./pages/studio-analytics/StudioIntelligenceHub";
import StudioRevenue from "./pages/studio-revenue";
import StudioSDK from "./pages/studio-sdk";
import StudioAPIKeys from "./pages/studio-api-keys";
import StudioApplicationForm from "./pages/studio-application-form";
import StudioApplicationPending from "./pages/studio-application-pending";
import StudioChatModeration from "./pages/studio-chat-moderation";
import StudioSocialDashboard from "./pages/studio-social-dashboard";
import EmailTracker from "./pages/email-tracker";

// Merch Pages
import MerchStore from "./pages/merch";
import ProductDetails from "./pages/merch/ProductDetails";
import Checkout from "./pages/merch/Checkout";
import MerchSuccess from "./pages/merch/MerchSuccess";

// Component to track page views
const PageViewTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page view when location changes
    trackPageView(location.pathname, document.title);
  }, [location]);

  return null;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <PageViewTracker />
        <MerchProvider>
          <RouterRoutes>
            {/* Define your routes here */}
            <Route path="/" element={<GameLibraryDashboard />} />
            <Route path="/browse" element={<GameLibraryDashboard />} />
            <Route path="/user-registration" element={<UserRegistration />} />
            <Route path="/user-login" element={<UserLogin />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/account-setup" element={<AccountSetup />} />
            <Route path="/game-library-dashboard" element={<GameLibraryDashboard />} />
            <Route path="/games/:gameId" element={<GameDetailLaunch />} />
            <Route path="/library" element={<MyLibrary />} />
            <Route path="/search" element={<SearchResults />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/subscription-management" element={<SubscriptionManagement />} />
            <Route path="/admin-dashboard" element={<AdminGuard><AdminDashboard /></AdminGuard>} />
            <Route path="/admin-user-management" element={<AdminGuard><AdminUserManagement /></AdminGuard>} />
            <Route path="/admin-moderation-dashboard" element={<AdminGuard><AdminModerationDashboard /></AdminGuard>} />
            <Route path="/admin-studio-management" element={<AdminGuard><AdminStudioManagement /></AdminGuard>} />
            <Route path="/admin-studio-details" element={<AdminGuard><AdminStudioDetails /></AdminGuard>} />
            <Route path="/admin-game-reviews" element={<AdminGuard><AdminGameReviews /></AdminGuard>} />
            <Route path="/admin-revenue-analytics" element={<AdminGuard><AdminRevenueAnalytics /></AdminGuard>} />
            <Route path="/admin-contact-management" element={<AdminGuard><AdminContactManagement /></AdminGuard>} />
            <Route path="/admin-email-templates" element={<AdminGuard><AdminEmailTemplates /></AdminGuard>} />
            <Route path="/admin-site-messages" element={<AdminGuard><AdminSiteMessages /></AdminGuard>} />
            <Route path="/user-profile-settings" element={<UserProfileSettings />} />
            <Route path="/game-content-management" element={<GameContentManagement />} />
            <Route path="/studio-revenue-analytics" element={<StudioRevenueAnalytics />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-use" element={<TermsOfUse />} />
            <Route path="/community-guidelines" element={<CommunityGuidelines />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/developers" element={<DevelopersPage />} />
            <Route path="/analytics-showcase" element={<AnalyticsShowcase />} />
            <Route path="/platform-comparison" element={<PlatformComparison />} />
            <Route path="/developers/monetization" element={<MonetizationShowcase />} />
            <Route path="/developers/iap-integration" element={<IAPIntegrationGuide />} />
            <Route path="/developers/payouts" element={<PayoutProcessGuide />} />
            <Route path="/studio-dashboard" element={<StudioDashboard />} />
            <Route path="/studio-games" element={<StudioGames />} />
            <Route path="/studio-game-upload" element={<StudioGameUpload />} />
            <Route path="/studio-analytics" element={<StudioAnalytics />} />
            <Route path="/studio/:studioId/analytics" element={<StudioIntelligenceHub />} />
            <Route path="/studio-revenue" element={<StudioRevenue />} />
            <Route path="/studio-sdk" element={<StudioSDK />} />
            <Route path="/studio-api-keys" element={<StudioAPIKeys />} />
            <Route path="/studio-application-form" element={<StudioApplicationForm />} />
            <Route path="/studio-application-pending" element={<StudioApplicationPending />} />
            <Route path="/studio/:studioId/chat-moderation" element={<StudioChatModeration />} />
            <Route path="/studio/:studioId/chat-moderation/:gameId" element={<StudioChatModeration />} />
            <Route path="/studio/:studioId/social" element={<StudioSocialDashboard />} />
            <Route path="/studio/:studioId/social/:gameId" element={<StudioSocialDashboard />} />

            {/* Merch Routes */}
            <Route path="/merch" element={<MerchStore />} />
            <Route path="/merch/product/:id" element={<ProductDetails />} />
            <Route path="/merch/checkout" element={<Checkout />} />
            <Route path="/merch/success" element={<MerchSuccess />} />

            {/* Email Tracker (internal) */}
            <Route path="/email-tracker" element={<EmailTracker />} />

            <Route path="*" element={<NotFound />} />
          </RouterRoutes>
        </MerchProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
